/**
 * WebCrypto primitives for the secret-chat subsystem.
 *
 * Everything in this file is intentionally low-level and stateless. It uses
 * only browser-built-in `crypto.subtle` primitives:
 *
 *   - ECDH on P-256 (key agreement)
 *   - ECDSA on P-256 with SHA-256 (long-term identity signatures)
 *   - HKDF-SHA-256 (key derivation)
 *   - AES-256-GCM (authenticated encryption)
 *   - HMAC-SHA-256 (chain key advancement)
 *
 * P-256 was chosen over Ed25519/X25519 because P-256 is universally supported
 * by `SubtleCrypto` across every browser we care about. It is the same curve
 * used by WebAuthn elsewhere in this codebase.
 *
 * No private key bytes are exported anywhere here. All key handles are
 * `CryptoKey` instances; persistence is handled by `storage.ts` via IDB
 * structured-clone of `CryptoKey`.
 */

/**
 * TS 5.7+ distinguishes `Uint8Array<ArrayBuffer>` from `Uint8Array<ArrayBufferLike>`
 * (the latter could wrap a SharedArrayBuffer). WebCrypto's `BufferSource` only
 * accepts the ArrayBuffer-backed flavour. We never use SharedArrayBuffers in
 * this subsystem, so we narrow the type at the boundary with this alias and
 * use it consistently below.
 */
export type Bytes = Uint8Array<ArrayBuffer>;

const subtle = (): SubtleCrypto => {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error(
      'WebCrypto (crypto.subtle) is not available in this environment. ' +
        'Secret chats require a modern browser over a secure (https/localhost) origin.',
    );
  }
  return crypto.subtle;
};

// ── Encoding helpers ────────────────────────────────────────────────────────

export function utf8ToBytes(s: string): Bytes {
  return new TextEncoder().encode(s) as Bytes;
}
export function bytesToUtf8(b: Bytes): string {
  return new TextDecoder().decode(b);
}

export function bytesToBase64Url(bytes: Uint8Array | ArrayBuffer): string {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let bin = '';
  for (let i = 0; i < u8.length; i += 1) bin += String.fromCharCode(u8[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64UrlToBytes(s: string): Bytes {
  const padded = s.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (padded.length % 4)) % 4;
  const bin = atob(padded + '='.repeat(padLen));
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) u8[i] = bin.charCodeAt(i);
  return u8;
}

export function concatBytes(...parts: Uint8Array[]): Bytes {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

export function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= a[i] ^ b[i];
  return diff === 0;
}

export function randomBytes(n: number): Bytes {
  const out = new Uint8Array(n);
  crypto.getRandomValues(out);
  return out;
}

// ── Identity signing keys (ECDSA P-256) ─────────────────────────────────────

export async function generateIdentitySigningKey(): Promise<CryptoKeyPair> {
  return subtle().generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, true, [
    'sign',
    'verify',
  ]);
}

export async function exportPublicSpki(key: CryptoKey): Promise<Bytes> {
  const buf = await subtle().exportKey('spki', key);
  return new Uint8Array(buf);
}

export async function importEcdsaPublicSpki(spki: Bytes): Promise<CryptoKey> {
  return subtle().importKey(
    'spki',
    spki,
    { name: 'ECDSA', namedCurve: 'P-256' },
    true,
    ['verify'],
  );
}

export async function importEcdhPublicSpki(spki: Bytes): Promise<CryptoKey> {
  return subtle().importKey(
    'spki',
    spki,
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    [],
  );
}

export async function ecdsaSign(privateKey: CryptoKey, data: Bytes): Promise<Bytes> {
  const sig = await subtle().sign(
    { name: 'ECDSA', hash: { name: 'SHA-256' } },
    privateKey,
    data,
  );
  return new Uint8Array(sig);
}

export async function ecdsaVerify(
  publicKey: CryptoKey,
  signature: Bytes,
  data: Bytes,
): Promise<boolean> {
  return subtle().verify(
    { name: 'ECDSA', hash: { name: 'SHA-256' } },
    publicKey,
    signature,
    data,
  );
}

// ── ECDH P-256 keypairs ─────────────────────────────────────────────────────

export async function generateEcdhKey(): Promise<CryptoKeyPair> {
  return subtle().generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']);
}

/**
 * Compute the raw 32-byte ECDH shared secret. The output is the X-coordinate
 * of the shared point, exactly as `deriveBits` returns it.
 */
export async function ecdhDeriveBits(
  privateKey: CryptoKey,
  peerPublicKey: CryptoKey,
): Promise<Bytes> {
  const bits = await subtle().deriveBits(
    { name: 'ECDH', public: peerPublicKey },
    privateKey,
    256,
  );
  return new Uint8Array(bits);
}

// ── HKDF-SHA-256 ────────────────────────────────────────────────────────────

export async function hkdf(
  ikm: Bytes,
  salt: Bytes,
  info: Bytes,
  length: number,
): Promise<Bytes> {
  const baseKey = await subtle().importKey('raw', ikm, 'HKDF', false, ['deriveBits']);
  const out = await subtle().deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt, info },
    baseKey,
    length * 8,
  );
  return new Uint8Array(out);
}

// ── HMAC-SHA-256 ────────────────────────────────────────────────────────────

export async function hmacSha256(key: Bytes, data: Bytes): Promise<Bytes> {
  const k = await subtle().importKey(
    'raw',
    key,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await subtle().sign('HMAC', k, data);
  return new Uint8Array(sig);
}

// ── AES-256-GCM ─────────────────────────────────────────────────────────────

export async function aesGcmEncrypt(
  rawKey: Bytes,
  iv: Bytes,
  plaintext: Bytes,
  associatedData: Bytes,
): Promise<Bytes> {
  if (rawKey.length !== 32) throw new Error('AES key must be 32 bytes');
  if (iv.length !== 12) throw new Error('GCM IV must be 12 bytes');
  const k = await subtle().importKey('raw', rawKey, { name: 'AES-GCM' }, false, ['encrypt']);
  const ct = await subtle().encrypt(
    { name: 'AES-GCM', iv, additionalData: associatedData, tagLength: 128 },
    k,
    plaintext,
  );
  return new Uint8Array(ct);
}

export async function aesGcmDecrypt(
  rawKey: Bytes,
  iv: Bytes,
  ciphertext: Bytes,
  associatedData: Bytes,
): Promise<Bytes> {
  if (rawKey.length !== 32) throw new Error('AES key must be 32 bytes');
  if (iv.length !== 12) throw new Error('GCM IV must be 12 bytes');
  const k = await subtle().importKey('raw', rawKey, { name: 'AES-GCM' }, false, ['decrypt']);
  const pt = await subtle().decrypt(
    { name: 'AES-GCM', iv, additionalData: associatedData, tagLength: 128 },
    k,
    ciphertext,
  );
  return new Uint8Array(pt);
}

// ── Phase 17: Double Ratchet KDF ────────────────────────────────────────────

/**
 * KDF_RK — Root Key derivation for the Double Ratchet.
 *
 * Inputs:
 *   rk     — 32-byte root key (used as HKDF salt)
 *   dhOut  — 32-byte raw ECDH shared secret
 *
 * Output: [newRK (32 bytes), chainKey (32 bytes)]
 *
 * This follows the Signal spec: HKDF with the root key as salt and the DH
 * output as input keying material, yielding 64 bytes split into the new root
 * key and the new chain key.
 */
export const HKDF_INFO_RATCHET = utf8ToBytes('IRChat-SecretChat-v2-ratchet');

export async function kdfRK(
  rk: Bytes,
  dhOut: Bytes,
): Promise<{ newRK: Bytes; chainKey: Bytes }> {
  const derived = await hkdf(dhOut, rk, HKDF_INFO_RATCHET, 64);
  return {
    newRK: derived.slice(0, 32) as Bytes,
    chainKey: derived.slice(32, 64) as Bytes,
  };
}

/**
 * Generate a fresh ECDH P-256 keypair for the DH ratchet step.
 * Alias of generateEcdhKey() — same algorithm, just explicit naming.
 */
export const generateRatchetKeyPair = generateEcdhKey;

// ── SHA-256 ─────────────────────────────────────────────────────────────────

export async function sha256(data: Bytes): Promise<Bytes> {
  return new Uint8Array(await subtle().digest('SHA-256', data));
}

// ── Capability check ────────────────────────────────────────────────────────

export function isWebCryptoAvailable(): boolean {
  try {
    return typeof crypto !== 'undefined' && !!crypto.subtle && !!crypto.getRandomValues;
  } catch {
    return false;
  }
}
